<script setup lang="ts">
    import { ref, computed } from 'vue'
    import { boolCompare, isNullOrWhitespace, numCompare } from '@common/Common'

    import type { ScoringVoteEntry } from '@Evaluation/AlternativesSurveys/Domain/readmodels'

    /* ---------------- props / emits ---------------- */

    const props = defineProps<{
        propIsMeetingManager: boolean
        propContextName: string
        propFirstAlternativeName: string
        propSecondAlternativeName: string
        propVotesEntries: ScoringVoteEntry[]
        propValue?: number | null
        propComment?: string | null
    }>()

    const emit = defineEmits<{
        (e: 'update-vote-value', value: number | null): void
        (e: 'update-vote-comment', comment: string): void
        (e: 'show-first-alternative-details'): void
        (e: 'show-second-alternative-details'): void
        (e: 'show-criterion-details'): void
    }>()

    /* ---------------- local state ---------------- */

    const ActiveTab = ref<'votes' | 'summary' | 'notes'>('votes')

    const ScaleView1 = [
        9, 8, 7, 6, 5, 4, 3, 2, 1,
        1 / 2, 1 / 3, 1 / 4, 1 / 5, 1 / 6, 1 / 7, 1 / 8, 1 / 9
    ].sort((a, b) => numCompare(a, b))

    /* ---------------- shortcuts ---------------- */

    const isMeetingManager = computed(() => props.propIsMeetingManager)
    const contextName = computed(() => props.propContextName)
    const firstAlternativeName = computed(() => props.propFirstAlternativeName)
    const secondAlternativeName = computed(() => props.propSecondAlternativeName)

    const votesEntries = computed(() => props.propVotesEntries)
    const value = computed<number | null>(() => props.propValue ?? null)
    const comment = computed<string | null>(() => props.propComment ?? null)

    /* ---------------- derived ---------------- */

    const VotesEntriesView = computed(() => votesEntries.value)

    const Comments = computed(() =>
        votesEntries.value.filter(e => !isNullOrWhitespace(e.Comment))
    )

    const VotesTabActive = computed(() => ActiveTab.value === 'votes')
    const SummaryTabActive = computed(() => ActiveTab.value === 'summary')
    const CommentsTabActive = computed(() => ActiveTab.value === 'notes')

    const EqualitySelected = computed(() => value.value === 1.0)
    const FirstSelected = computed(() => value.value != null && value.value > 1.0)
    const SecondSelected = computed(() => value.value != null && value.value < 1.0)

    /* ---------------- averages ---------------- */

    const Average = computed<number | null>(() =>
    {
        const valid = votesEntries.value.filter(e => e.Value != null && e.VoteStrength > 0)
        if (!valid.length) return null

        const sumWeights = valid.reduce((s, e) => s + e.VoteStrength, 0)

        let avg = 1.0
        for (const e of valid)
        {
            avg *= Math.pow(e.Value, e.VoteStrength / sumWeights)
        }

        return avg
    })

    const AverageDescription = computed(() =>
    {
        if (Average.value == null) return 'N/A'
        return Average.value < 1
            ? (1 / Average.value).toFixedIfNeeded(2)
            : Average.value.toFixedIfNeeded(2)
    })

    /* ---------------- scale ---------------- */

    const ScaleView = computed<number[]>(() =>
    {
        const base = [...ScaleView1]
        if (Average.value != null)
        {
            if (!base.some(v => Math.round(v * 100) === Math.round(Average.value * 100)))
            {
                base.push(Average.value)
            }
        }
        return base.sort((a, b) => numCompare(a, b)).reverse()
    })

    const LeftScaleValuesCount = computed(() => ScaleView.value.filter(v => v > 1).length)
    const RightScaleValuesCount = computed(() => ScaleView.value.filter(v => v < 1).length)

    const IntegerValue = computed<number | null>(() =>
    {
        if (!value.value) return null
        return Math.round((value.value < 1 ? 1 / value.value : value.value) * 100) / 100
    })

    /* ---------------- labels ---------------- */

    function GetLabel(v: number): string
    {
        switch (v)
        {
            case 2: return 'Weakly'
            case 3: return 'Moderately'
            case 4: return 'Moderately +'
            case 5: return 'Strongly'
            case 6: return 'Strongly +'
            case 7: return 'Very Strongly'
            case 8: return 'Very Strongly +'
            case 9: return 'Extremely'
            default: return ''
        }
    }

    const FirstBtnLabel = computed(() =>
        FirstSelected.value
            ? `${GetLabel(IntegerValue.value)} (${IntegerValue.value})`
            : 'More preferred'
    )

    const SecondBtnLabel = computed(() =>
        SecondSelected.value
            ? `${GetLabel(IntegerValue.value)} (${IntegerValue.value})`
            : 'More preferred'
    )

    /* ---------------- histogram ---------------- */

    const HistogramValues = computed(() =>
    {
        const voteValues = votesEntries.value.filter(e => e.Value != null).map(e => e.Value)
        const uniqueValues = new Set([...ScaleView1, ...voteValues])
        const orderedValues = [...uniqueValues].sort((a, b) => b - a)

        let maxCount = 0
        for (const v of orderedValues)
        {
            maxCount = Math.max(maxCount, voteValues.filter(x => x === v).length)
        }

        return orderedValues.map(v =>
        {
            const count = voteValues.filter(x => x === v).length
            return {
                Value: v,
                Count: count,
                PercentOfMax: maxCount ? Math.round((count / maxCount) * 100) : 0
            }
        })
    })

    /* ---------------- scale rendering helpers ---------------- */

    function FormatValue(v: number): number
    {
        return v < 1 ? 1 / v : v
    }

    function GetColor(v: number): string
    {
        const r = Math.round(v * 100) / 100
        if (r === 1) return 'bg-color-green2'
        if (r > 1) return 'bg-color-blue2'
        return 'bg-color-red2'
    }

    function GetScaleItemType(scaleValue: number, voteValue: number | null): string | null
    {
        if (voteValue == null) return 'Empty'
        if (scaleValue < 1 && voteValue > 1) return 'Empty'
        if (scaleValue > 1 && voteValue < 1) return 'Empty'

        let x: number | null = null

        if (scaleValue <= 1 && voteValue <= 1) x = (1 / scaleValue) - (1 / voteValue)
        if (scaleValue >= 1 && voteValue >= 1) x = scaleValue - voteValue

        x = Math.round(x * 100) / 100

        if (x === 0) return 'Filled'
        if (x >= 1 || x <= -1) return 'Empty'
        if (x > -1 && x < 0) return 'Hidden'
        if (x > 0 && x < 1) return 'DoubleFilled'

        return null
    }

    function GetCssClass(scaleValue: number, voteValue: number | null): string | null
    {
        const type = GetScaleItemType(scaleValue, voteValue)
        if (type === 'Empty') return 'EmptyScaleItem'
        if (type === 'Hidden') return 'HiddenScaleItem'
        if (type === 'Filled') return `FilledScaleItem ${GetColor(voteValue)}`
        if (type === 'DoubleFilled') return `DoubleFilledScaleItem ${GetColor(voteValue)}`
        return null
    }

    function GetScaleItemText(scaleValue: number, voteValue: number | null): string
    {
        const type = GetScaleItemType(scaleValue, voteValue)
        return type === 'DoubleFilled'
            ? FormatValue(voteValue).toFixedIfNeeded(2)
            : FormatValue(scaleValue).toLocaleString()
    }

    /* ---------------- actions ---------------- */

    function updateValue(v: number | null)
    {
        emit('update-vote-value', v)
    }

    function updateComment(c: string)
    {
        emit('update-vote-comment', c)
    }

    function showFirstAlternativeDetails()
    {
        emit('show-first-alternative-details')
    }

    function showSecondAlternativeDetails()
    {
        emit('show-second-alternative-details')
    }

    function showCriterionDetails()
    {
        emit('show-criterion-details')
    }
</script>


<template>
    <div>
        <p class="text-center lead" style="font-size: 1.5rem;">
            In the context of <a class="text-primary" @click="showCriterionDetails" title="Show details">{{contextName}}</a>,
            which alternative do you prefer: <br />
            <a class="text-primary" @click="showFirstAlternativeDetails" title="Show details">{{firstAlternativeName}}</a> or
            <a class="text-primary" @click="showSecondAlternativeDetails" title="Show details">{{secondAlternativeName}}</a>?
        </p>

        <template v-if="isMeetingManager === true">
            <div class="card card-collapsable mt-1">
                <div class="card-header pointer" data-toggle="collapse" href="#cardQuestion">
                    Group vote
                </div>
                <div id="cardQuestion" class="card-body collapse show pt-4">
                    <div class="PairwiseComparisonEditor">

                        <div class="row">
                            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12">
                                <div class="mt1 ItemDetailsBox bg-color-blue3">
                                    <h4>{{firstAlternativeName}}</h4>
                                </div>
                                <div class="btn-group ItemButton">
                                    <button type="button" class="btn btn-block dropdown-toggle" data-toggle="dropdown" :class="FirstSelected === true ? 'btn-success' : 'btn-outline-secondary'">
                                        <i class="fa-regular" aria-hidden="true" :class="FirstSelected === true ? 'fa-square-check' : 'fa-square'"></i> {{FirstBtnLabel}}
                                    </button>
                                    <div class="dropdown-menu">
                                        <h4 class="dropdown-header">How strong is your preference?</h4>
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item" @click="updateValue(2)">Weak (2)</a>
                                        <a class="dropdown-item" @click="updateValue(3)">Moderate (3)</a>
                                        <a class="dropdown-item" @click="updateValue(4)">Moderate + (4)</a>
                                        <a class="dropdown-item" @click="updateValue(5)">Strong (5)</a>
                                        <a class="dropdown-item" @click="updateValue(6)">Strong + (6)</a>
                                        <a class="dropdown-item" @click="updateValue(7)">Very strong (7)</a>
                                        <a class="dropdown-item" @click="updateValue(8)">Very strong + (8)</a>
                                        <a class="dropdown-item" @click="updateValue(9)">Extreme (9)</a>
                                    </div>
                                </div>
                            </div>

                            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12">
                                <div class="mt1 ItemDetailsBox bg-color-red3">
                                    <h4>{{secondAlternativeName}}</h4>
                                </div>
                                <div class="btn-group ItemButton">
                                    <button type="button" class="btn btn-block dropdown-toggle" data-toggle="dropdown" :class="SecondSelected === true ? 'btn-success' : 'btn-outline-secondary'">
                                        <i class="fa-regular" aria-hidden="true" :class="SecondSelected === true ? 'fa-square-check' : 'fa-square'"></i> {{SecondBtnLabel}}
                                    </button>
                                    <div class="dropdown-menu">
                                        <h4 class="dropdown-header">How strong is your preference?</h4>
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item" @click="updateValue(1.0/2.0)">Weak (2)</a>
                                        <a class="dropdown-item" @click="updateValue(1.0/3.0)">Moderate (3)</a>
                                        <a class="dropdown-item" @click="updateValue(1.0/4.0)">Moderate + (4)</a>
                                        <a class="dropdown-item" @click="updateValue(1.0/5.0)">Strong (5)</a>
                                        <a class="dropdown-item" @click="updateValue(1.0/6.0)">Strong + (6)</a>
                                        <a class="dropdown-item" @click="updateValue(1.0/7.0)">Very strong (7)</a>
                                        <a class="dropdown-item" @click="updateValue(1.0/8.0)">Very strong + (8)</a>
                                        <a class="dropdown-item" @click="updateValue(1.0/9.0)">Extreme (9)</a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-lg-6 offset-lg-3 col-md-6 offset-md-3 col-sm-6 offset-sm-3 col-xs-12">
                                <button type="button" class="btn btn-block btn-equal mt1" @click="updateValue(1.0)" :class="EqualitySelected === true ? 'btn-success' : 'btn-outline-secondary'">
                                    <i class="fa-regular" aria-hidden="true" :class="EqualitySelected === true ? 'fa-square-check' : 'fa-square'"></i>
                                    <span>Equally preferred (1)</span>
                                </button>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-lg-6 offset-lg-3 col-md-6 offset-md-3 col-sm-6 offset-sm-3 col-xs-12">
                                <button type="button" class="btn btn-block mt1" @click="updateValue(null)" :class="value == null ? 'btn-success' : 'btn-outline-secondary'">
                                    <i class="fa-regular" aria-hidden="true" :class="value == null ? 'fa-square-check' : 'fa-square'"></i>
                                    Follow the average (default)
                                </button>
                            </div>
                        </div>

                        <div style="margin-top: 30px;">
                            <h5 style="margin-bottom: 5px;">Comment:</h5>
                            <textarea rows="5" :value.trim="comment" @input="updateComment($event.target.value)" class="form-control" placeholder="Comment..."></textarea>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template v-if="isMeetingManager === false">
            <div class="PairwiseComparisonEditor">

                <div class="row">
                    <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12">
                        <div class="mt1 ItemDetailsBox bg-color-blue3">
                            <h4>{{firstAlternativeName}}</h4>
                        </div>
                        <div class="btn-group ItemButton">
                            <button type="button" class="btn btn-block dropdown-toggle" data-toggle="dropdown" :class="FirstSelected === true ? 'btn-success' : 'btn-outline-secondary'">
                                <i class="fa-regular" aria-hidden="true" :class="FirstSelected === true ? 'fa-square-check' : 'fa-square'"></i> {{FirstBtnLabel}}
                            </button>
                            <div class="dropdown-menu">
                                <h4 class="dropdown-header">How strong is your preference?</h4>
                                <div class="dropdown-divider"></div>
                                <a class="dropdown-item" @click="updateValue(2)">Weak (2)</a>
                                <a class="dropdown-item" @click="updateValue(3)">Moderate (3)</a>
                                <a class="dropdown-item" @click="updateValue(4)">Moderate + (4)</a>
                                <a class="dropdown-item" @click="updateValue(5)">Strong (5)</a>
                                <a class="dropdown-item" @click="updateValue(6)">Strong + (6)</a>
                                <a class="dropdown-item" @click="updateValue(7)">Very strong (7)</a>
                                <a class="dropdown-item" @click="updateValue(8)">Very strong + (8)</a>
                                <a class="dropdown-item" @click="updateValue(9)">Extreme (9)</a>
                            </div>
                        </div>
                    </div>

                    <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12">
                        <div class="mt1 ItemDetailsBox bg-color-red3">
                            <h4>{{secondAlternativeName}}</h4>
                        </div>
                        <div class="btn-group ItemButton">
                            <button type="button" class="btn btn-block dropdown-toggle" data-toggle="dropdown" :class="SecondSelected === true ? 'btn-success' : 'btn-outline-secondary'">
                                <i class="fa-regular" aria-hidden="true" :class="SecondSelected === true ? 'fa-square-check' : 'fa-square'"></i> {{SecondBtnLabel}}
                            </button>
                            <div class="dropdown-menu">
                                <h4 class="dropdown-header">How strong is your preference?</h4>
                                <div class="dropdown-divider"></div>
                                <a class="dropdown-item" @click="updateValue(1.0/2.0)">Weak (2)</a>
                                <a class="dropdown-item" @click="updateValue(1.0/3.0)">Moderate (3)</a>
                                <a class="dropdown-item" @click="updateValue(1.0/4.0)">Moderate + (4)</a>
                                <a class="dropdown-item" @click="updateValue(1.0/5.0)">Strong (5)</a>
                                <a class="dropdown-item" @click="updateValue(1.0/6.0)">Strong + (6)</a>
                                <a class="dropdown-item" @click="updateValue(1.0/7.0)">Very strong (7)</a>
                                <a class="dropdown-item" @click="updateValue(1.0/8.0)">Very strong + (8)</a>
                                <a class="dropdown-item" @click="updateValue(1.0/9.0)">Extreme (9)</a>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-lg-6 offset-lg-3 col-md-6 offset-md-3 col-sm-6 offset-sm-3 col-xs-12">
                        <button type="button" class="btn btn-block btn-equal mt1" @click="updateValue(1.0)" :class="EqualitySelected === true ? 'btn-success' : 'btn-outline-secondary'">
                            <i class="fa-regular" aria-hidden="true" :class="EqualitySelected === true ? 'fa-square-check' : 'fa-square'"></i>
                            <span>Equally preferred (1)</span>
                        </button>
                    </div>
                </div>

                <div class="row">
                    <div class="col-lg-6 offset-lg-3 col-md-6 offset-md-3 col-sm-6 offset-sm-3 col-xs-12">
                        <div v-if="value != null" class="text-center">
                            <button type="button" class="btn btn-link mt1" @click="updateValue(null)">
                                <i class="fa fa-times" aria-hidden="true"></i> Clear answer
                            </button>
                        </div>
                    </div>
                </div>

                <div style="margin-top: 30px;">
                    <h5 style="margin-bottom: 5px;">Comment:</h5>
                    <textarea rows="5" :value.trim="comment" @input="updateComment($event.target.value)" class="form-control" placeholder="Comment..."></textarea>
                </div>
            </div>
        </template>




        <div class="PairwiseComparisonVotesBrowserView mt-4" style="min-height: 500px;" v-if="isMeetingManager === true || votesEntries?.length">
            <ul class="nav nav-tabs">
                <li class="nav-item">
                    <a class="nav-link" @click="ActiveTab = 'votes'" :class="{ 'active': VotesTabActive }">Votes</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" @click="ActiveTab = 'summary'" :class="{ 'active': SummaryTabActive }">Chart</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" @click="ActiveTab = 'notes'" :class="{ 'active': CommentsTabActive }">Comments</a>
                </li>
            </ul>

            <div class="tab-content">
                <div v-if="VotesTabActive" :class="{'active': VotesTabActive}" class="tab-pane pt-2">

                    <div class="table-responsive">
                        <table class="table table-striped">
                            <thead>
                                <tr>
                                    <th style="border-top: 0;"></th>
                                    <th class="clearfix" style="min-width: 510px; border-top: 0;">
                                        <div class="clearfix pull-right" style="width: 85%;">
                                            <div class="pull-left">
                                                {{firstAlternativeName}}
                                            </div>
                                            <div class="pull-right">
                                                {{secondAlternativeName}}
                                            </div>
                                            <div class="text-center" style="margin: 0 auto; width: 50px;">
                                                vs
                                            </div>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr style="text-align: center;">
                                    <td style="text-align: left;">
                                        <strong>Average ({{AverageDescription}})</strong>
                                    </td>
                                    <td style="padding: 0;">
                                        <ul class="clearfix" style="list-style: none; margin: 0; padding: 0;">
                                            <li v-for="scaleValue in ScaleView1" :class="GetCssClass(scaleValue, Average)">
                                                {{GetScaleItemText(scaleValue, Average)}}
                                            </li>
                                        </ul>
                                    </td>
                                </tr>

                                <tr style="text-align: center;" v-for="e in VotesEntriesView">
                                    <td style="text-align: left;">
                                        {{e.VotesSourceName ?? '???'}}
                                    </td>
                                    <td style="padding: 0;">
                                        <ul class="clearfix" style="list-style: none; margin: 0; padding: 0;">
                                            <li v-for="scaleValue in ScaleView1" :class="GetCssClass(scaleValue, e.Value)">
                                                {{GetScaleItemText(scaleValue, e.Value)}}
                                            </li>
                                        </ul>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                </div>

                <div v-if="SummaryTabActive" :class="{ 'active': SummaryTabActive }" class="tab-pane pt-2">

                    <div class="table-responsive">
                        <table class="table">
                            <tbody>
                                <tr style="text-align: center;" class="bottom noborder">
                                    <td v-for="v in HistogramValues" :key="v.Value" style="height: 400px; border: none; padding-top: 20px;">
                                        <div v-if="v.Count" v-html="v.Count"></div>
                                        <div :style="{'height': v.PercentOfMax + '%'}" :class="GetColor(v.Value)" class="bg-color-blue2"></div>
                                    </td>
                                </tr>
                                <tr style="text-align: center;">
                                    <td v-for="v in HistogramValues" :key="v.Value" style="width: 6%;">
                                        {{ FormatValue(v.Value).toFixedIfNeeded(2)}}
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th :colspan="LeftScaleValuesCount" style="text-align: left;">{{firstAlternativeName}}</th>
                                    <th style="text-align: center;">vs</th>
                                    <th :colspan="RightScaleValuesCount" style="text-align: right;">{{secondAlternativeName}}</th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                </div>

                <div v-if="CommentsTabActive" :class="{ 'active': CommentsTabActive }" class="tab-pane pt-2">
                    <blockquote class="blockquote" v-for="c in Comments">
                        <p class="mb-0">{{c.Comment}}</p>
                        <footer class="blockquote-footer">{{c.VotesSourceName ?? '???'}}</footer>
                    </blockquote>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .PairwiseComparisonEditor .btn, .PairwiseComparisonEditor textarea {
        border-radius: 0;
    }

    .PairwiseComparisonEditor .mt1 {
        margin-top: 20px !important;
    }

    .PairwiseComparisonEditor .mt2 {
        margin-top: 40px !important;
    }

    .PairwiseComparisonEditor div.ItemDetailsBox {
        border: 1px solid #ddd;
        border-bottom: 0;
        height: 90px;
        max-height: 90px;
        overflow-y: auto;
        padding: 15px 10px;
        background-color: #f5f5f5;
    }

        .PairwiseComparisonEditor div.ItemDetailsBox h4 {
            padding: 0;
            margin: 0;
            text-align: center;
        }

    .PairwiseComparisonEditor div.ItemButton {
        width: 100%;
        padding: 0px;
    }

        .PairwiseComparisonEditor div.ItemButton a.btn {
            height: 38px;
            padding-top: 8px;
        }

        .PairwiseComparisonEditor div.ItemButton div.dropdown-menu {
            width: 100%;
        }

    .PairwiseComparisonEditor a.btn-equal {
        height: 38px;
        padding-top: 8px;
        width: 100%;
    }

    .PairwiseComparisonEditor a.btn-geometric-mean {
        height: 38px;
        padding-top: 8px;
        width: 100%;
    }

    .PairwiseComparisonVotesBrowserView tr.bottom td {
        vertical-align: bottom !important;
    }


    .PairwiseComparisonVotesBrowserView ul li.EmptyScaleItem {
        display: block;
        float: right;
        min-width: 30px;
        width: 5%;
        padding: 8px;
    }

    .PairwiseComparisonVotesBrowserView ul li.FilledScaleItem {
        display: block;
        float: right;
        min-width: 30px;
        width: 5%;
        padding: 8px;
        color: white;
    }

    .PairwiseComparisonVotesBrowserView ul li.HiddenScaleItem {
        display: none;
    }

    .PairwiseComparisonVotesBrowserView ul li.DoubleFilledScaleItem {
        display: block;
        float: right;
        min-width: 60px;
        width: 10%;
        padding: 8px;
        color: white;
    }
</style>